/**
 * @file useTimeWindow.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-07-21
 * @modified 2026-07-21
 * @description Shared 12-month viewing window for the cost / sale-price history
 *              charts, with back/forward paging. The current period (offset 0)
 *              ends at the start of next month so today — and a short projected
 *              tail — sit inside it; each step pages a full 12 months. Charts
 *              filter their data to [startTs, endTs] and only draw a projection
 *              when isCurrent is true. Callers pass the data extent so paging can
 *              stop once there's no older data to show.
 */

import { useState, useMemo, useCallback } from 'react';

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

/**
 * @param {[number|null, number|null]} extent  [minTs, maxTs] of the data (ms). Used
 *        only to decide when "back" should stop; null disables the back limit.
 * @param {object} [opts]
 * @param {number} [opts.windowMonths=12]  window length and page step in months.
 */
export default function useTimeWindow(extent, { windowMonths = 12 } = {}) {
  // 0 = current period; +1 = one full window older; and so on.
  const [offset, setOffset] = useState(0);

  const minTs = extent && extent[0] != null ? extent[0] : null;

  // Anchor everything to month boundaries so paging lands on clean months.
  const baseEnd = useMemo(() => {
    const today = new Date();
    return addMonths(startOfMonth(today), 1); // start of next month
  }, []);
  const todayTs = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, []);

  const endDate = useMemo(() => addMonths(baseEnd, -offset * windowMonths), [baseEnd, offset, windowMonths]);
  const startDate = useMemo(() => addMonths(endDate, -windowMonths), [endDate, windowMonths]);

  const startTs = startDate.getTime();
  const endTs = endDate.getTime();

  const canForward = offset > 0;
  // Only allow paging back while there's recorded data older than the window start.
  const canBack = minTs != null && minTs < startTs;

  const goBack = useCallback(() => setOffset(o => o + 1), []);
  const goForward = useCallback(() => setOffset(o => Math.max(0, o - 1)), []);
  const reset = useCallback(() => setOffset(0), []);

  // "Aug 2025 – Jul 2026" — the inclusive month span actually on screen.
  const label = useMemo(() => {
    const fmt = (d) => d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    const lastMonth = addMonths(endDate, -1); // endDate is exclusive (start of month after)
    return `${fmt(startDate)} – ${fmt(lastMonth)}`;
  }, [startDate, endDate]);

  return {
    startTs, endTs, startDate, endDate,
    offset, isCurrent: offset === 0,
    canBack, canForward, goBack, goForward, reset,
    label, todayTs
  };
}
